/**
 * Public stats from the admin Next.js app: `GET /api/public/landing-stats`.
 *
 * The admin dashboard uses locale-prefixed **pages** (`/fr/...`, `/en/...`, `/ar/...`).
 * API routes live at the app root — they are **not** under a locale segment.
 *
 * Correct:  `https://<admin-origin>/api/public/landing-stats`
 * Wrong:     `https://<admin-origin>/fr/api/public/landing-stats`
 *
 * Set `VITE_LANDING_STATS_API_URL` in `.env` to the correct URL above.
 *
 * Optional hero snapshot (same JSON response) — flat keys for easy admin APIs:
 * - `heroDieselStatus`, `heroGasolineStatus`: `"available"` | `"low"` | `"out"` (case-insensitive; also `disponible`, `rupture`, …)
 * - `heroDieselPrice`, `heroGasolinePrice`: MRU/L (number)
 * - `heroNearbyAvailableCount`: e.g. stations with fuel near a reference point ..
 */
export type HeroFuelStatus = "available" | "low" | "out" | "unknown"

export interface HeroFuelSnapshot {
  status: HeroFuelStatus
  priceMruPerLiter: number | null
}

export interface LandingStatsHeroSnapshot {
  diesel: HeroFuelSnapshot
  gasoline: HeroFuelSnapshot
  nearbyAvailableCount: number | null
}

export interface LandingStats {
  downloads: number
  rating: number
  stations: number
  wilayas: number
  appStoreUrl: string
  playStoreUrl: string
  updatedAt: string
  /** Live fuel snapshot for the landing hero; omit to use static marketing fallbacks in the UI. */
  heroSnapshot?: LandingStatsHeroSnapshot
}

export const defaultLandingStats: LandingStats = {
  downloads: 1000,
  rating: 4.8,
  stations: 50,
  wilayas: 15,
  appStoreUrl: "https://apps.apple.com",
  playStoreUrl: "https://play.google.com",
  updatedAt: new Date().toISOString(),
}

function norm(s: string): string {
  return s.trim().toLowerCase()
}

function parseFuelStatus(raw: unknown): HeroFuelStatus {
  if (raw == null || typeof raw !== "string") return "unknown"
  const n = norm(raw)
  if (["available", "disponible", "ok", "open"].includes(n)) return "available"
  if (["low", "faible", "limited"].includes(n)) return "low"
  if (["out", "empty", "rupture", "épuisé", "epuise", "unavailable", "indisponible", "closed"].includes(n)) return "out"
  return "unknown"
}

function parseOptionalPrice(raw: unknown): number | null {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return null
  return raw
}

function parseHeroSnapshot(o: Record<string, unknown>): LandingStatsHeroSnapshot | undefined {
  const hasAny =
    o.heroDieselStatus != null ||
    o.heroGasolineStatus != null ||
    o.heroDieselPrice != null ||
    o.heroGasolinePrice != null ||
    o.heroNearbyAvailableCount != null
  if (!hasAny) return undefined
  return {
    diesel: {
      status: parseFuelStatus(o.heroDieselStatus),
      priceMruPerLiter: parseOptionalPrice(o.heroDieselPrice),
    },
    gasoline: {
      status: parseFuelStatus(o.heroGasolineStatus),
      priceMruPerLiter: parseOptionalPrice(o.heroGasolinePrice),
    },
    nearbyAvailableCount: typeof o.heroNearbyAvailableCount === "number" && Number.isFinite(o.heroNearbyAvailableCount)
      ? Math.max(0, Math.floor(o.heroNearbyAvailableCount))
      : null,
  }
}

export async function fetchLandingStats(): Promise<LandingStats> {
  const url = import.meta.env.VITE_LANDING_STATS_API_URL as string | undefined
  if (!url?.trim()) {
    return defaultLandingStats
  }
  const res = await fetch(url, { cache: "no-store" })
  if (!res.ok) {
    return defaultLandingStats
  }
  const data: unknown = await res.json()
  if (!data || typeof data !== "object") {
    return defaultLandingStats
  }
  const o = data as Record<string, unknown>
  const heroSnapshot = parseHeroSnapshot(o)
  return {
    downloads: typeof o.downloads === "number" ? o.downloads : defaultLandingStats.downloads,
    rating: typeof o.rating === "number" ? o.rating : defaultLandingStats.rating,
    stations: typeof o.stations === "number" ? o.stations : defaultLandingStats.stations,
    wilayas: typeof o.wilayas === "number" ? o.wilayas : defaultLandingStats.wilayas,
    appStoreUrl: typeof o.appStoreUrl === "string" ? o.appStoreUrl : defaultLandingStats.appStoreUrl,
    playStoreUrl: typeof o.playStoreUrl === "string" ? o.playStoreUrl : defaultLandingStats.playStoreUrl,
    updatedAt: typeof o.updatedAt === "string" ? o.updatedAt : new Date().toISOString(),
    ...(heroSnapshot ? { heroSnapshot } : {}),
  }
}
